import { createSelector } from '@reduxjs/toolkit';
import { selectors as dataGridTableSelectors } from '../Modules/dataGridTable';
import { selectors as filterSelectors } from '../Modules/filters';
import { isEmpty } from 'lodash';
import { getItemOrDefaultToValue, getPersonName } from 'utils/details';
import { getRiskLevelValue } from 'utils/helper/zone';
import get from 'lodash/get';
import { PAGE_URLS } from 'utils/constants';
import { riskColors } from 'utils/constants/risks';

export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);
export const getTotalEntries = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => {
    if (!rowsCount) {
      return 0;
    }
    return rowsCount;
  },
);
export const getFilters = createSelector(
  filterSelectors.getFilterValues,
  filterValues => {
    return filterValues;
  },
);
// gets the rowID of the selected region row
export const getSelectedRowID = createSelector(
  dataGridTableSelectors.getSelectedRowID,
  selectedRowID => selectedRowID,
);
export const getTransformedRegionList = createSelector(
  dataGridTableSelectors.getGridTableList,
  list => {
    if (isEmpty(list)) return [];
    return list.map(data => ({
      pageName: PAGE_URLS.REGION.link,
      id: data.id,
      regionName: getItemOrDefaultToValue(data, 'name', '-'),
      manager: data.manager != null ? getPersonName(data.manager.general) : '-',
      noOfSecurityCompany:
        data.numSecurityCompanies != null
          ? getItemOrDefaultToValue(data, 'numSecurityCompanies', 0)
          : 0,
      noOfZone:
        data.numZones != null
          ? getItemOrDefaultToValue(data, 'numZones', 0)
          : 0,
      noOfGuard:
        data.numGuards != null
          ? getItemOrDefaultToValue(data, 'numGuards', 0)
          : 0,
      noOfResponder:
        data.numResponders != null
          ? getItemOrDefaultToValue(data, 'numResponders', 0)
          : 0,
      riskLevel:
        data.currentRisk != null
          ? getRiskLevelValue(data.currentRisk, 'rating', '-')
          : '-',
      currency: getItemOrDefaultToValue(data, 'currency', '-'),
      defaultSecurityCompanyName: getItemOrDefaultToValue(
        data,
        'defaultSecurityCompanyName',
        '-',
      ),
      boundary: data.boundary
        ? getItemOrDefaultToValue(data.boundary, 'points', []).map(bound => ({
            lat: bound.latitude,
            lng: bound.longitude,
          }))
        : [],
      centreOfGravity: getItemOrDefaultToValue(
        data.boundary,
        'centreOfGravity',
        null,
      ),
    }));
  },
);
export const getApiRegionDetails = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  region => {
    if (isEmpty(region)) return {};
    return {
      ...region,
      ...region.currentRisk,
      ...(region.boundary !== null && {
        locations: [
          {
            id: region.id,
            editable: true,
            draggable: false,
            color: riskColors['REGION'],
            isFilled: true,
            paths: getItemOrDefaultToValue(region.boundary, 'points').map(
              point => ({
                lat: point.latitude,
                lng: point.longitude,
              }),
            ),
          },
        ],
        paths: get(region, 'boundary.points', []),
        centreOfGravity: getItemOrDefaultToValue(
          region.boundary,
          'centreOfGravity',
          null,
        ),
      }),
    };
  },
);
export const getTransformedRegionDetails = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  region => {
    if (isEmpty(region)) return {};
    return {
      generalInfo: {
        name: {
          label: 'Name',
          type: 'text',
          value: getItemOrDefaultToValue(region, 'name', '-'),
        },
        currency: {
          label: 'Currency',
          type: 'text',
          value: getItemOrDefaultToValue(region, 'currency', '-'),
        },
        defaultTimeZone: {
          label: 'Default time zone',
          type: 'text',
          value: getItemOrDefaultToValue(region, 'defaultTimeZone'),
        },
        regionRisk: {
          label: 'Current Risk',
          type: 'text',
          value: get(region, 'currentRisk.rating', '-'),
        },
        regionRiskContext: {
          label: 'Risk Context',
          type: 'text',
          value: get(region, 'currentRisk.context', '-'),
        },
      },
      others: {
        locations: {
          value:
            region.boundary != null
              ? [
                  {
                    editable: true,
                    draggable: false,
                    color: riskColors['REGION'],
                    isFilled: true,
                    paths: getItemOrDefaultToValue(
                      region.boundary,
                      'points',
                    ).map(point => ({
                      lat: point.latitude,
                      lng: point.longitude,
                    })),
                  },
                ]
              : [],
        },
        id: {
          value: region.id,
        },
      },
    };
  },
);
